<template>
  <div class="page home-page">
    <div class="container">
      <h1 class="hidden">Home Page</h1>
      <MainSlider
        v-if="isShowMainSlider || isShowBaseMainSlider"
        :items="isShowMainSlider ? slides : baseSlides"
      ></MainSlider>
      <SiteFeatures
        v-if="isShowSiteFeatures"
        :features="features"
      ></SiteFeatures>
      <SeasonalSlider
        v-if="isShowSeasonalSlider"
        :items="seasonalSlides"
      ></SeasonalSlider>
      <Divider />
      <FeaturedCollection
        v-if="isShowFeaturedCollection"
        :is-measure-performance="true"
        :items="featuredCollection"
      ></FeaturedCollection>
      <client-only
        :is-show="componentStore.homePage.isShowBestSelling"
      >
        <template v-if="isShowBestSelling">
        <Divider />
        <BestSelling
          :collections="bestSelling"
          :showProductTM="showBestSellingProductTM"
        ></BestSelling>
        </template>
      </client-only>
      <client-only
        :is-show="componentStore.homePage.isShowTopics"
      >
        <template v-if="isShowTopics">
        <Divider />
        <Topics :items="topics"></Topics>
        </template>
      </client-only>
      <client-only
        :is-show="componentStore.homePage.isShowReviews"
      >
        <template v-if="isShowReviews">
        <Divider />
        <CustomerReviews :reviews="reviews"></CustomerReviews>
        </template>
      </client-only>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onServerPrefetch, useSSRContext } from 'vue'
import { useRoute } from 'vue-router'

import MainSlider from './MainSlider.vue'
import SiteFeatures from './SiteFeatures.vue'
import SeasonalSlider from './SeasonalSlider.vue'
import FeaturedCollection from './FeaturedCollection.vue'
import BestSelling from './BestSelling.vue'
import Divider from '@/components/Divider.vue'
import useSettingStore from '@/store/setting'
import useComponentStore from '@/store/component'
import { runTaskOnIdleTime } from '@/utils/idlize'
import Topics from './Topics.vue'
import CustomerReviews from './CustomerReviews.vue'
import { isOnServer } from '@/utils/ssr'
import { appendToHead } from '@/composables/appendToHead'
import { SSR_CONTEXT_APPEND_TO_HEAD_META_TAGS } from '@/utils/constants'
import { QUERY_STRING_NO_CACHE } from '@/utils'
import { useSettings } from '@/composables/settings'

const { filterSettingsByUserSegment } = useSettings()
const route = useRoute()
const settingStore = useSettingStore()
const componentStore = useComponentStore()
const baseSlides = computed(() =>
  filterSettingsByUserSegment(
    settingStore.basePages?.homePage?.mainSlider?.slides
  )
)
const slides = computed(() =>
  filterSettingsByUserSegment(settingStore.pages?.homePage?.mainSlider?.slides)
)
const features = computed(
  () => settingStore.pages?.homePage?.siteFeatures?.features
)
const seasonalSlides = computed(
  () => settingStore.pages?.homePage?.seasonalSlider?.slides
)
const featuredCollection = computed(
  () => settingStore.pages?.homePage?.featuredCollection?.items
)
const bestSelling = computed(() =>
  filterSettingsByUserSegment(
    settingStore.pages?.homePage?.bestSelling?.collections
  )
)
const showBestSellingProductTM = computed(
  () => settingStore.shop?.searchProductTM
)
const topics = computed(() => settingStore.pages?.homePage?.topics?.groupTopics)
const reviews = computed(
  () => settingStore.pages?.homePage?.customerReviews?.items
)

const isShowMainSlider = computed(
  () => settingStore.pages?.homePage?.mainSlider?.isShow
)
const isShowBaseMainSlider = computed(
  () => settingStore.basePages?.homePage?.mainSlider?.isShow
)
const isShowSiteFeatures = computed(
  () => settingStore.pages?.homePage?.siteFeatures?.isShow
)
const isShowSeasonalSlider = computed(
  () => settingStore.pages?.homePage?.seasonalSlider?.isShow
)
const isShowFeaturedCollection = computed(
  () => settingStore.pages?.homePage?.featuredCollection?.isShow
)
const isShowBestSelling = computed(
  () => settingStore.pages?.homePage?.bestSelling?.isShow
)
const isShowTopics = computed(
  () => settingStore.pages?.homePage?.topics?.isShow
)
const isShowReviews = computed(
  () => settingStore.pages?.homePage?.customerReviews?.isShow
)

const ssrContext = isOnServer && useSSRContext()

onServerPrefetch(async () => {
  // inject meta tags into head tag
  if (ssrContext) {
    appendToHead(
      `<meta property="og:type" content="website" />`,
      SSR_CONTEXT_APPEND_TO_HEAD_META_TAGS,
      ssrContext
    )
  }
})

onMounted(() => {
  const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]
  runTaskOnIdleTime(async () => {
    if (isShowFeaturedCollection.value && !featuredCollection.value?.length) {
      await settingStore.loadSettings(true)
    }
  })
  runTaskOnIdleTime(() => {
    componentStore.homePage.isShowBestSelling = true
  })
  runTaskOnIdleTime(() => {
    componentStore.homePage.isShowTopics = true
  })
  runTaskOnIdleTime(() => {
    componentStore.homePage.isShowReviews = true
  })
  runTaskOnIdleTime(async () => {
    if (
      !isForceLoadNewData &&
      isShowBestSelling.value &&
      bestSelling.value?.length
    ) {
      return
    }
    if (!isShowBestSelling.value) {
      return
    }
    await settingStore.loadSettings(true)
  })
})
</script>

<style lang="scss">
.home-page {
  .carousel {
    &__item {
      width: 100%;
    }
    .video.carousel__video {
      aspect-ratio: 1;
      @include media-md-up {
        aspect-ratio: 3.1199;
      }
    }
  }
}
</style>
